import React, { useState } from 'react'
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Container,
    Grid,
    Stack,
    TextField,
    Typography,
} from '@mui/material'
import {
    Email as EmailIcon,
    SupportAgent as SupportIcon,
} from '@mui/icons-material'
import { useLanguage } from '../contexts/LanguageContext'
import api from '../services/api'

const ORANGE = '#FF8A00'
const cardSx = {
    borderRadius: 6,
    background: 'rgba(16, 11, 38, 0.92)',
    border: '1px solid rgba(255,255,255,0.08)',
    boxShadow: '0 24px 60px rgba(0,0,0,0.22)',
}
const fieldSx = {
    '& .MuiInputBase-root': { bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 4, color: '#fff' },
    '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.62)' },
    '& .MuiInputLabel-root.Mui-focused': { color: ORANGE },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.12)' },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,138,0,0.38)' },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: ORANGE },
}

const ContactUs = () => {
    const { lang } = useLanguage()
    const isFr = lang === 'fr'
    const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' })
    const [sent, setSent] = useState(false)
    const [sending, setSending] = useState(false)
    const [error, setError] = useState('')
    const [ticketNo, setTicketNo] = useState('')

    const set = (key) => (event) => setForm((prev) => ({ ...prev, [key]: event.target.value }))

    const submit = async () => {
        setSending(true)
        setError('')
        setTicketNo('')
        try {
            const response = await api.post('/support/tickets', {
                ...form,
                source: 'website',
                service_domain: 'general_support',
            })
            setTicketNo(response?.data?.ticket_no || '')
            setSent(true)
            setTimeout(() => setSent(false), 3500)
            setForm({ name: '', email: '', subject: '', message: '' })
        } catch (requestError) {
            setError(requestError?.response?.data?.message || (isFr ? 'Impossible d envoyer le message pour le moment.' : 'Unable to send the message right now.'))
        } finally {
            setSending(false)
        }
    }
    const routingItems = isFr
        ? [
            'Paiements, factures et envois d argent',
            'Recus, preuves et historique manquant',
            'Profil, reglages et acces au compte',
            'Support entreprise, commerce ou facture',
        ]
        : [
            'Payments, bills, and send-money issues',
            'Receipts, proof trail, and missing history',
            'Profile, settings, and account access',
            'Business, merchant, or invoice support',
        ]

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#09061B', py: { xs: 4, md: 6 } }}>
            <Container maxWidth="lg">
                <Card
                    sx={{
                        ...cardSx,
                        mb: 3,
                        overflow: 'hidden',
                        background:
                            'linear-gradient(135deg, rgba(17,10,40,0.98) 0%, rgba(70,36,188,0.96) 58%, rgba(255,138,0,0.16) 100%)',
                    }}
                >
                    <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                        <Stack spacing={1.4}>
                            <Stack direction="row" spacing={1.2} alignItems="center">
                                <Chip label={isFr ? 'Support MojoPayr' : 'MojoPayr Support'} sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: '#fff', fontWeight: 900 }} />
                                {sent ? <Chip label={ticketNo ? `${isFr ? 'Ticket cree' : 'Ticket created'} ${ticketNo}` : isFr ? 'Message envoye' : 'Message sent'} sx={{ bgcolor: 'rgba(255,138,0,0.12)', color: ORANGE, fontWeight: 900 }} /> : null}
                            </Stack>
                            <Typography sx={{ color: '#fff', fontWeight: 950, fontSize: { xs: 30, md: 42 } }}>
                                {isFr ? 'Nous contacter' : 'Contact Us'}
                            </Typography>
                            <Typography sx={{ color: 'rgba(255,255,255,0.80)', maxWidth: 820 }}>
                                {isFr
                                    ? 'Contactez l equipe MojoPayr pour les questions sur les paiements, le support, les recus, les parcours entreprise, ou les problemes de compte. Restez simple et nous vous orienterons vers le bon service.'
                                    : 'Reach the MojoPayr team with questions about payments, support, receipts, business flows, or account issues. Keep it simple and we will route it to the right place.'}
                            </Typography>
                        </Stack>
                    </CardContent>
                </Card>

                <Grid container spacing={3}>
                    <Grid item xs={12} lg={7}>
                        <Card sx={cardSx}>
                            <CardContent sx={{ p: { xs: 2.8, md: 3 } }}>
                                <Typography sx={{ color: '#fff', fontWeight: 950, fontSize: 22, mb: 2 }}>
                                    {isFr ? 'Envoyer un message' : 'Send a message'}
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <TextField fullWidth label={isFr ? 'Votre nom' : 'Your name'} value={form.name} onChange={set('name')} sx={fieldSx} />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField fullWidth label="Email" value={form.email} onChange={set('email')} sx={fieldSx} />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField fullWidth label={isFr ? 'Sujet' : 'Subject'} value={form.subject} onChange={set('subject')} sx={fieldSx} />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField fullWidth multiline minRows={6} label="Message" value={form.message} onChange={set('message')} sx={fieldSx} />
                                    </Grid>
                                </Grid>
                                {error ? <Typography sx={{ color: '#FF8F8F', mt: 1.6, fontWeight: 800 }}>{error}</Typography> : null}

                                <Button
                                    variant="contained"
                                    onClick={submit}
                                    disabled={sending || !form.name || !form.email || !form.subject || !form.message}
                                    sx={{
                                        mt: 2.5,
                                        borderRadius: 999,
                                        px: 3,
                                        py: 1.2,
                                        textTransform: 'none',
                                        fontWeight: 900,
                                        bgcolor: ORANGE,
                                        '&:hover': { bgcolor: '#ff9c2a' },
                                    }}
                                >
                                    {sending ? (isFr ? 'Envoi...' : 'Sending...') : (isFr ? 'Envoyer le message' : 'Send message')}
                                </Button>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} lg={5}>
                        <Stack spacing={3}>
                            <Card sx={cardSx}>
                                <CardContent sx={{ p: 3 }}>
                                    <Stack direction="row" spacing={1.1} alignItems="center" sx={{ mb: 1.3 }}>
                                        <SupportIcon sx={{ color: ORANGE }} />
                                        <Typography sx={{ color: '#fff', fontWeight: 950, fontSize: 22 }}>{isFr ? 'Orientation du support' : 'Support routing'}</Typography>
                                    </Stack>
                                    <Typography sx={{ color: 'rgba(255,255,255,0.70)', mb: 2 }}>
                                        {isFr ? 'Plus votre message est clair, plus MojoPayr peut le diriger rapidement vers le bon service.' : 'The cleaner your message, the faster MojoPayr can route it to the right area.'}
                                    </Typography>
                                    <Stack spacing={1.1}>
                                        {routingItems.map((item) => (
                                            <Box key={item} sx={{ p: 1.4, borderRadius: 4, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                                                <Typography sx={{ color: 'rgba(255,255,255,0.76)' }}>{item}</Typography>
                                            </Box>
                                        ))}
                                    </Stack>
                                </CardContent>
                            </Card>

                            <Card sx={cardSx}>
                                <CardContent sx={{ p: 3 }}>
                                    <Stack direction="row" spacing={1.1} alignItems="center" sx={{ mb: 1.3 }}>
                                        <EmailIcon sx={{ color: ORANGE }} />
                                        <Typography sx={{ color: '#fff', fontWeight: 950, fontSize: 22 }}>{isFr ? 'Contacts directs' : 'Direct contacts'}</Typography>
                                    </Stack>
                                    <Stack spacing={1.1}>
                                        <ContactRow label="Support" value="support@mojopayr.com" />
                                        <ContactRow label={isFr ? 'Presse' : 'Press'} value="press@mojopayr.com" />
                                        <ContactRow label="Base" value="Owings Mills, Maryland" />
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Stack>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    )
}

const ContactRow = ({ label, value }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
        <Typography sx={{ color: 'rgba(255,255,255,0.56)', fontWeight: 800 }}>{label}</Typography>
        <Typography sx={{ color: '#fff', fontWeight: 800, textAlign: 'right' }}>{value}</Typography>
    </Box>
)

export default ContactUs
